// Registry of figure components exposed as markdown container directives.
// Use a directive in any `.md` file as:
//
//     :::ComputeScale
//     :::
//
// and the named entry below renders in place. Names are case-sensitive and
// must be unique across the codebase's `containerDirective` handlers.
//
// To add a new shortcut: import the default export from its figure folder
// and add a `Name: <Component />` line to FIGURES_DIRECTIVES.
//
// Data-driven charts pull their props from figures-data/baked.ts — a static
// TS module produced by `npx tsx scripts/bakeFiguresData.ts` from the xlsx /
// csv / json sources under figures-data/. Re-run the bake script after editing
// any source data file.

import type { ReactNode } from 'react';
// Every figure component loads lazily in its own chunk: this registry is
// imported by Markdown.tsx (i.e. by EVERY page that renders markdown), and
// with static imports the entire figure library (~140KB gz) shipped in First
// Load JS even on pages that render none of them. next/dynamic keeps SSR
// output for the figures while the client fetches only the chunks for
// directives that actually appear on the page.
import dynamic from 'next/dynamic';
// Exceptions to the lazy-loading rule above: the two simple-takeoff sketches
// are tiny pure static SVGs (no client hooks, a few hundred bytes each) and
// render inside LINK-TOOLTIP cards, which mount on hover. A next/dynamic chunk
// there costs a network round-trip on first hover — a visible ~1s stall before
// the sketch appears. Importing them statically bakes them into the markdown
// bundle so they're ready the instant the tooltip opens, at trivial byte cost.
import SimpleSmoothTakeoff from './simple-takeoff/SimpleSmoothTakeoff';
import SimpleLumpyTakeoff from './simple-takeoff/SimpleLumpyTakeoff';
import SimpleAiInvestment from './simple-investment/SimpleAiInvestment';
import SimpleCveSpike from './simple-cve-spike/SimpleCveSpike';
import SimpleSigmoidBranches from './simple-sigmoid/SimpleSigmoidBranches';
import SimpleParadigmTimeline from './simple-paradigms/SimpleParadigmTimeline';
import {
  economyShareData,
  laborIncomeShareData,
  outputShareData,
  taskAutomationData,
  incomeData,
  planAGdp,
  defaultWorldGdp,
  companyRevenue,
  ai2027Revenue,
  taxRevenueData,
  counterfactualData,
  computeStatusAnchors,
  computeTrajectoryAnchors,
  planAPopulation,
  defaultWorldPopulation,
} from './figures-data/baked';

// ── Compute / infrastructure diagrams ────────────────────────────────────
const ComputeAllocationDiagram2 = dynamic(() => import('./compute-allocation/ComputeAllocationDiagram2'));
const ComputeFlowChart = dynamic(() => import('./compute-flow/ComputeFlowChart'));
const ComputeFlowFullChart = dynamic(() => import('./compute-flow/ComputeFlowFullChart'));
const ComputeGrowthChart = dynamic(() => import('./compute-growth/ComputeGrowthChart'));
const ComputeGrowthChart2035 = dynamic(() => import('./compute-growth/ComputeGrowthChart2035'));
const ComputeGrowthChart2035Small = dynamic(() => import('./compute-growth/ComputeGrowthChart2035Small'));
const ComputeScaleDiagram = dynamic(() => import('./compute-scale/ComputeScaleDiagram'));
const ComputeScaleDiagram2 = dynamic(() => import('./compute-scale/ComputeScaleDiagram2'));
const ComputeScaleChart = dynamic(() => import('./compute-scale/ComputeScaleChart'));
const ComputeStockChart = dynamic(() => import('./compute-stock/ComputeStockChart'));
const ComputeStatus2029 = dynamic(() => import('./compute-status-2029/ComputeStatus2029'));
const ChipDeclarationResult = dynamic(() => import('./compute-status-2029/ChipDeclarationResult'));
const AIDatacenters2029 = dynamic(() => import('../compute-breakdowns/DatacenterPolicyFigure'));
const ChipRestrictions2029 = dynamic(() => import('./chip-restrictions/ChipRestrictions2029'));
const ChipRestrictions2032 = dynamic(() => import('./chip-restrictions/ChipRestrictions2032'));
const ConsumerCompute2029 = dynamic(() => import('./consumer-compute/ConsumerCompute2029'));
const ConsumerCompute2032 = dynamic(() => import('./consumer-compute/ConsumerCompute2032'));
const InferenceRetrofitDiagram = dynamic(() => import('./inference-retrofit/InferenceRetrofitDiagram'));
const WorldComputeChart = dynamic(() => import('./world-compute/WorldComputeChart'));
const WorldComputeChart3 = dynamic(() => import('./world-compute-3/WorldComputeChart3'));
const WCMLithoPool = dynamic(() => import('./world-compute/WCM_LithoPool'));
const WCMHistory = dynamic(() => import('./world-compute/WCM_History'));
const WCMFuture = dynamic(() => import('./world-compute/WCM_Future'));
const WCMEffective = dynamic(() => import('./world-compute/WCM_Effective'));
const WCMFinalPicture = dynamic(() => import('./world-compute/WCM_FinalPicture'));
const WCMWaferShareFigure = dynamic(() => import('./world-compute/WCM_WaferShareFigure'));
const WCMFleetTable = dynamic(() => import('./world-compute/WCM_FleetTable'));
const WCMPassesByCategory = dynamic(() => import('./world-compute/WCM_PassesByCategory'));
const WCMH100eByCategory = dynamic(() => import('./world-compute/WCM_H100eByCategory'));
const WCMImpliedPerWafer = dynamic(() => import('./world-compute/WCM_ImpliedPerWafer'));
const WCMClusterSplit = dynamic(() => import('./world-compute/WCM_ClusterSplit'));
const WCMLithoSplitFigure = dynamic(() => import('./world-compute/WCM_LithoSplitFigure'));
const WorldComputeRectangle = dynamic(() => import('./world-compute/WorldComputeRectangle'));
const DatacenterSizesTool = dynamic(() => import('./world-compute/DatacenterSizesTool'));
const CapexFundingFigure = dynamic(() => import('./world-compute/CapexFundingFigure'));
const AICapexVsMilitaryFigure = dynamic(() => import('./world-compute/AICapexVsMilitaryFigure'));
const ComputeCategoriesSketch = dynamic(() => import('./world-compute/ComputeCategoriesSketch'));
const ChinaComputeFigure = dynamic(() => import('./world-compute/ChinaComputeFigure'));
const H100eBuildoutFigure = dynamic(() => import('./world-compute/H100eBuildoutFigure'));
const CostEfficiencyFigure = dynamic(() => import('./world-compute/CostEfficiencyFigure'));
const EUVSurchargeFigure = dynamic(() => import('./world-compute/EUVSurchargeFigure'));
const EUVFleetTable = dynamic(() => import('./world-compute/EUVFleetTable'));
const NaiveBaselineFigure = dynamic(() => import('./world-compute/NaiveBaselineFigure'));
const TrendToggleFigure = dynamic(() => import('./world-compute/TrendToggleFigure'));
const Deal2030Figure = dynamic(() => import('./world-compute/Deal2030Figure'));
const ImpliedInvestmentFigure = dynamic(() => import('./world-compute/ImpliedInvestmentFigure'));
const PowerEfficiencyTrend = dynamic(() => import('./world-compute/PowerEfficiencyTrend'));
const CompanyConcentration = dynamic(() => import('./world-compute/CompanyConcentration'));
const CompanyConcentrationTreemap = dynamic(() => import('./world-compute/CompanyConcentrationTreemap'));
const AICopies = dynamic(() => import('./world-compute/AICopies'));
const AIModelSize = dynamic(() => import('./world-compute/AIModelSize'));
const InferenceHardware = dynamic(() => import('./world-compute/InferenceHardware'));
const AISpeed = dynamic(() => import('./world-compute/AISpeed'));
const AICopiesSpeedTable = dynamic(() => import('./world-compute/AICopiesSpeedTable'));
const NesovModelSizeTable = dynamic(() => import('./world-compute/NesovModelSizeTable'));
const ModelSizeBuildupTable = dynamic(() => import('./world-compute/ModelSizeBuildupTable'));
const GPContextTable = dynamic(() => import('./world-compute/GPContextTable'));
const UtilizationTable = dynamic(() => import('./world-compute/UtilizationTable'));
const UsefulnessCalculator = dynamic(() => import('./world-compute/UsefulnessCalculator'));
const ComputeAllocation = dynamic(() => import('./world-compute/ComputeAllocation'));
const AllComputeEffectiveFigure = dynamic(() => import('./world-compute/AllComputeEffectiveFigure'));
const RegionalOwnershipFigure = dynamic(() => import('./world-compute/RegionalOwnershipFigure'));

// ── Energy / climate / minerals ──────────────────────────────────────────
const DecouplingElasticityChart = dynamic(() => import('./energy-gdp-history/DecouplingElasticityChart'));
const EnergyGdpIndexChart = dynamic(() => import('./energy-gdp-history/EnergyGdpIndexChart'));
const EnergyGdpLogLogChart = dynamic(() => import('./energy-gdp-history/EnergyGdpLogLogChart'));
const JonesAutomationDiagram = dynamic(() => import('./jones-automation/JonesAutomationDiagram'));
const AifmEmbed = dynamic(() => import('./aifm-embed/AifmEmbed'));
const SimpleExplorerEmbed = dynamic(() => import('./simple-explorer/SimpleExplorerEmbed'));
const MineralReservesChart = dynamic(() => import('./mineral-reserves/MineralReservesChart'));
const MineralYearsLeftChart = dynamic(() => import('./mineral-reserves/MineralYearsLeftChart'));
const SocialCostCarbonChart = dynamic(() => import('./energy-temperature/SocialCostCarbonChart'));

// ── Safety / governance / verification ───────────────────────────────────
const AlignmentOverTimeDiagram = dynamic(() => import('./alignment-over-time/AlignmentOverTimeDiagram'));
const CovertComputeSankey = dynamic(() => import('./covert-compute/CovertComputeSankey'));
const CovertStrategyOverview = dynamic(() => import('./covert-project/CovertStrategyOverview'));
const ChipTracingTree = dynamic(() => import('./covert-project/ChipTracingTree'));
const ChipLifecycleFlow = dynamic(() => import('./covert-project/ChipLifecycleFlow'));
const EpistemicsInterventionsGrid = dynamic(() => import('./epistemics-interventions/EpistemicsInterventionsGrid'));
const AIForEpistemicsInterventions = dynamic(() => import('./ai-for-epistemics-interventions/AIForEpistemicsInterventions'));
const BasinOfSanityDiagram = dynamic(() => import('./basin-of-sanity/BasinOfSanityDiagram'));
const AIForEpistemicsFlowchart = dynamic(() => import('./ai-for-epistemics-flowchart/AIForEpistemicsFlowchart'));
const AssuranceLevelDiagram = dynamic(() => import('./assurance-level/AssuranceLevelDiagram'));
const AssuranceCurveChart = dynamic(() => import('./assurance-curve/AssuranceCurveChart'));
const AssuranceCurveCompute = dynamic(() => import('./assurance-curve/AssuranceCurveCompute'));
const InferenceOnlyAssurance = dynamic(() => import('./assurance-curve/InferenceOnlyAssurance'));
const TakeoverRiskPlanA = dynamic(() => import('./takeover-risk/TakeoverRiskPlanA'));
const TakeoverRiskPlanD = dynamic(() => import('./takeover-risk/TakeoverRiskPlanD'));
const RogueDeploymentCurve = dynamic(() => import('./assurance-curve/RogueDeploymentCurve'));
const MaxUndetectedRogue = dynamic(() => import('./assurance-curve/MaxUndetectedRogue'));
const LoggingGranularityDiagram = dynamic(() => import('./logging-granularity/LoggingGranularityDiagram'));
const ResearchTitrationDiagram = dynamic(() => import('./research-titration/ResearchTitrationDiagram'));
const ResearchTitrationApproaches = dynamic(() => import('./research-titration/ResearchTitrationApproaches'));
const WorkloadChunkingDiagram = dynamic(() => import('./workload-chunking/WorkloadChunkingDiagram'));
const GovernanceAirgapDiagram = dynamic(() => import('./governance-airgap/GovernanceAirgapDiagram'));
const RadicalTransparencyDiagram = dynamic(() => import('./radical-transparency/RadicalTransparencyDiagram'));
const TransparencyBenefitsDiagram = dynamic(() => import('./radical-transparency/TransparencyBenefitsDiagram'));
const SafetyCaseBuildingBlocks = dynamic(() => import('./safety-case/SafetyCaseBuildingBlocks'));
const SafetyCaseChart = dynamic(() => import('./safety-case/SafetyCaseChart'));
const SecurityTransparencyDiagram = dynamic(() => import('./security-transparency/SecurityTransparencyDiagram'));
const VerificationDecisionTree = dynamic(() => import('./verification-decision-tree/VerificationDecisionTree'));
const VerificationFlowDiagram = dynamic(() => import('./verification-flow/VerificationFlowDiagram'));
const DatacenterFlowDiagram = dynamic(() => import('./datacenter-flow/DatacenterFlowDiagram'));
const DatacenterArchitectureDiagram = dynamic(() => import('./datacenter-flow/DatacenterArchitectureDiagram'));
const DatacenterArchitectureFilteredDiagram = dynamic(() => import('./datacenter-flow/DatacenterArchitectureFilteredDiagram'));
const VerificationTradeoffsDiagram = dynamic(() => import('./verification-tradeoffs/VerificationTradeoffsDiagram'));

// ── Handoff timing / plan capabilities ───────────────────────────────────
const AbsoluteRiskChart = dynamic(() => import('./handoff-timing/AbsoluteRiskChart'));
const DealBreakdownByYearChart = dynamic(() => import('./handoff-timing/DealBreakdownByYearChart'));
const HandoffRiskByYearChart = dynamic(() => import('./handoff-timing/HandoffRiskByYearChart'));
const OptimalHandoffChart = dynamic(() => import('./handoff-timing/OptimalHandoffChart'));
const HandoffOrNotWidget = dynamic(() => import('./handoff-or-not/HandoffOrNotWidget'));
const PlanCapabilitiesChart = dynamic(() => import('./plan-capabilities/PlanCapabilitiesChart'));
const PlanCapabilitiesChartMilestones = dynamic(() => import('./plan-capabilities/PlanCapabilitiesChartMilestones'));
const PlanAvsPlanDUpliftChart = dynamic(() => import('./plan-capabilities/PlanAvsPlanDUpliftChart'));
const RacingTakeoffUpliftChart = dynamic(() => import('./plan-capabilities/RacingTakeoffUpliftChart'));
const SlowdownComparisonChart = dynamic(() => import('./plan-capabilities/SlowdownComparisonChart'));
const PlanComparisonTable = dynamic(() => import('./plan-comparison/PlanComparisonTable'));
const AuthorSlopeChart = dynamic(() => import('./outcomes-by-author/AuthorSlopeChart'));
import { LIKELIHOOD, OUTCOMES } from './outcomes-by-author/authorEstimatesGenerated';
const Stage1TargetSelectionChart = dynamic(() => import('./stage1-target-selection/Stage1TargetSelectionChart'));
const DealDeclineCdfChart = dynamic(() => import('./deal-decline-cdf/DealDeclineCdfChart'));
const DissolutionTrajectoryChart = dynamic(() => import('./dissolution-trajectory/DissolutionTrajectoryChart'));
const Stage1TargetSelectionValueLostChart = dynamic(() => import('./stage1-target-selection/Stage1TargetSelectionValueLostChart'));
const Stage1TargetSelectionV1Chart = dynamic(() => import('./stage1-target-selection-v1/Stage1TargetSelectionV1Chart'));

// ── Timeline / horizon ───────────────────────────────────────────────────
const TimeHorizon80pChart = dynamic(() => import('./time-horizon-80p/TimeHorizon80pChart'));
const TimelineLineDiagram = dynamic(() => import('./timeline/TimelineLineDiagram'));
const TimelineLineDetailedClean = dynamic(() => import('./timeline/TimelineLineDetailedClean'));
const TimelineQuestionsDiagram = dynamic(() => import('./timeline-questions/TimelineQuestionsDiagram'));

// ── Data-driven (props sourced from figures-data/baked.ts) ───────────────
const EconomyShareChart = dynamic(() => import('./economy-share/EconomyShareChart'));
const LaborIncomeShareChart = dynamic(() => import('./labor-income-share/LaborIncomeShareChart'));
const OutputShareChart = dynamic(() => import('./output-share/OutputShareChart'));
const TaskAutomationChart = dynamic(() => import('./task-automation/TaskAutomationChart'));
const TaskAutomationLitChart = dynamic(() => import('./task-automation-lit/TaskAutomationLitChart'));
const TaskAutomationAIEraChart = dynamic(() => import('./task-automation-aiera/TaskAutomationAIEraChart'));
const IncomeDistributionPDFOverlay = dynamic(() => import('./income-distribution/IncomeDistributionPDFOverlay'));
const EnergyGdpIndexChartProjected = dynamic(() => import('./energy-gdp-history/EnergyGdpIndexChartProjected'));
const EnergyGdpLogLogChartProjected = dynamic(() => import('./energy-gdp-history/EnergyGdpLogLogChartProjected'));
const EnergyModelSummary = dynamic(() => import('./energy-gdp-history/EnergyModelSummary'));
const CovertProjectMap = dynamic(() => import('./covert-project/CovertProjectMap'));
const EnergyTempByMethodChart = dynamic(() => import('./energy-temperature/EnergyTempByMethodChart'));
const Co2WarmingTcreChart = dynamic(() => import('./energy-temperature/Co2WarmingTcreChart'));
const Co2WarmingTimeSeriesChart = dynamic(() => import('./energy-temperature/Co2WarmingTimeSeriesChart'));
const CompanyRevenueChart = dynamic(() => import('./company-revenue/CompanyRevenueChart'));
const CompanyRevenueChartLinear = dynamic(() => import('./company-revenue/CompanyRevenueChartLinear'));
const CompanyRevenue2026Chart = dynamic(() => import('./company-revenue/CompanyRevenue2026Chart'));
const GwpHistoryChart = dynamic(() => import('./gwp-history/GwpHistoryChart'));
const UsEmploymentChart = dynamic(() => import('./us-employment/UsEmploymentChart'));
const UsMedianIncomeChart = dynamic(() => import('./us-median-income/UsMedianIncomeChart'));
const UsWorkersIncomeCombined = dynamic(() => import('./us-workers-income/UsWorkersIncomeCombined'));
const OpenSourceSpectrum = dynamic(() => import('./open-source-spectrum/OpenSourceSpectrum'));
const TransparencySpectrum = dynamic(() => import('./radical-transparency/TransparencySpectrum'));
const TaxRevenueChart = dynamic(() => import('./tax-revenue/TaxRevenueChart'));
const CapAndTradeScenarios = dynamic(() => import('./cap-and-trade/CapAndTradeScenarios'));
const ComputeStatusGlobal2029 = dynamic(() => import('./compute-status-2029/ComputeStatusGlobal2029'));
const ComputeStatusPattern2029 = dynamic(() => import('./compute-status-2029/ComputeStatusPattern2029'));
const ComputeTrajectoryChart = dynamic(() => import('./compute-trajectory/ComputeTrajectoryChart'));
const LaborPopulationChart = dynamic(() => import('./labor-population/LaborPopulationChart'));

export const FIGURES_DIRECTIVES: Record<string, ReactNode> = {
  // Compute / infrastructure
  ComputeAllocation2:       <ComputeAllocationDiagram2 />,
  ComputeFlow:              <ComputeFlowChart />,
  ComputeFlowFull:          <ComputeFlowFullChart />,
  ComputeGrowth:            <ComputeGrowthChart />,
  ComputeGrowth2035:        <ComputeGrowthChart2035 />,
  ComputeGrowth2035Small:   <ComputeGrowthChart2035Small />,
  ComputeScale:             <ComputeScaleDiagram />,
  ComputeScale2:            <ComputeScaleDiagram2 />,
  ComputeScaleChart:        <ComputeScaleChart />,
  ComputeStock:             <ComputeStockChart />,
  ComputeStatus2029:        <ComputeStatus2029 />,
  ChipDeclarationResult:    <ChipDeclarationResult />,
  ChipDeclarationResultGlobal: <ChipDeclarationResult includeRoW undeclCells={2} />,
  AIDatacenters2029:        <AIDatacenters2029 />,
  AIDatacenters2029Plain:   <AIDatacenters2029 showPolicy={false} showSingleDevice />,
  AIDatacenters2029Hybrid:  <AIDatacenters2029 showSingleDevice showNonAiConsumer />,
  ChipRestrictions2029:     <ChipRestrictions2029 />,
  ChipRestrictions2032:     <ChipRestrictions2032 />,
  ConsumerCompute2029:      <ConsumerCompute2029 />,
  ConsumerCompute2032:      <ConsumerCompute2032 />,
  InferenceRetrofit:        <InferenceRetrofitDiagram />,
  WorldCompute:             <WorldComputeChart />,
  WorldCompute3:            <WorldComputeChart3 />,
  WCM_LithoPool:            <WCMLithoPool />,
  WCM_History:              <WCMHistory />,
  WCM_Future:               <WCMFuture />,
  WCM_Effective:            <WCMEffective />,
  WCM_FinalPicture:         <WCMFinalPicture />,
  WCM_FleetTable:           <WCMFleetTable />,
  WCM_PassesByCategory:     <WCMPassesByCategory />,
  WCM_H100eByCategory:      <WCMH100eByCategory />,
  WCM_ImpliedPerWafer:      <WCMImpliedPerWafer />,
  WCM_ClusterSplit:         <WCMClusterSplit />,
  WCM_LithoSplit:           <WCMLithoSplitFigure />,
  WCM_WaferShareHistory:    <WCMWaferShareFigure phase="history" />,
  WCM_WaferShareForecast:   <WCMWaferShareFigure phase="forecast" />,
  ComputeInTheWorld:        <WorldComputeRectangle />,
  DatacenterSizes:          <DatacenterSizesTool />,
  CapexFunding:             <CapexFundingFigure />,
  AICapexVsMilitary:        <AICapexVsMilitaryFigure />,
  // Alias: the story gdoc tags this figure `AICapexVsMilitaryFigure`, so the
  // generated content resolves across re-imports without hand edits.
  AICapexVsMilitaryFigure:  <AICapexVsMilitaryFigure />,
  ComputeCategoriesSketch:  <ComputeCategoriesSketch />,
  ChinaCompute:             <ChinaComputeFigure />,
  H100eBuildout:            <H100eBuildoutFigure />,
  CostEfficiency:           <CostEfficiencyFigure />,
  EUVSurcharge:             <EUVSurchargeFigure />,
  EUVFleetTable:            <EUVFleetTable />,
  NaiveBaselineFigure:      <NaiveBaselineFigure />,
  TrendToggle:              <TrendToggleFigure />,
  Deal2030:                 <Deal2030Figure />,
  ImpliedInvestment:        <ImpliedInvestmentFigure />,
  PowerEfficiencyTrend:     <PowerEfficiencyTrend />,
  CompanyConcentration:     <CompanyConcentration />,
  CompanyConcentrationTreemap: <CompanyConcentrationTreemap />,
  ComputeAllocation:        <ComputeAllocation />,
  AICopies:                 <AICopies />,
  AIModelSize:              <AIModelSize />,
  InferenceHardware:        <InferenceHardware />,
  AISpeed:                  <AISpeed />,
  AICopiesSpeedTable:       <AICopiesSpeedTable />,
  NesovModelSizeTable:      <NesovModelSizeTable />,
  ModelSizeBuildupTable:    <ModelSizeBuildupTable />,
  GPContextTable:           <GPContextTable />,
  UtilizationTable:         <UtilizationTable />,
  UsefulnessCalculator:     <UsefulnessCalculator />,
  AllComputeEffective:      <AllComputeEffectiveFigure />,
  RegionalOwnership:        <RegionalOwnershipFigure />,

  // Energy / climate / minerals
  DecouplingElasticity:     <DecouplingElasticityChart />,
  EnergyGdpIndex:           <EnergyGdpIndexChart />,
  EnergyGdpLogLog:          <EnergyGdpLogLogChart />,
  MineralReserves:          <MineralReservesChart />,
  MineralYearsLeft:         <MineralYearsLeftChart />,
  ResourceCeilings:         <MineralYearsLeftChart />,
  JonesAutomation:          <JonesAutomationDiagram />,
  AifmEmbed:                <AifmEmbed />,
  SimpleExplorerEmbed: <SimpleExplorerEmbed />,
  FullExplorerBanner: (
    <a
      href="/supplements/econ-explorer"
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: 'block', textDecoration: 'none', margin: '24px 0' }}
    >
      <div style={{ border: '1px solid #d9d7c8', borderLeft: '4px solid #2A623D', borderRadius: '10px', background: '#fffef8', padding: '18px 22px' }}>
        <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '20px', fontWeight: 600, color: '#1f4a2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
          Open the Full Explorer <span aria-hidden="true">↗</span>
        </div>
        <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '14px', color: '#555', marginTop: '5px' }}>
          Our full economic growth explorer, that models the Plan A scenario in a more hand crafted way, and features e.g., capital, robots, prices, land, and energy.
        </div>
      </div>
    </a>
  ),
  SocialCostCarbon:         <SocialCostCarbonChart />,

  // Safety / governance / verification
  AlignmentOverTime:        <AlignmentOverTimeDiagram />,
  CovertComputeSankey:      <CovertComputeSankey />,
  // Interactive remake of the covert-ai-projects overview diagram (image11);
  // added to the supplement by putting `:::CovertOverview` + `:::` in the
  // source gdoc, in place of the exported image.
  CovertOverview:           <CovertStrategyOverview />,
  // Native remakes of the covert-ai-projects chip-tracing diagrams (image13 /
  // image10 exports), substituted for those images via
  // IMAGE_FIGURE_REPLACEMENTS (app/common/Markdown.tsx).
  ChipTracingTree:          <ChipTracingTree />,
  ChipLifecycle:            <ChipLifecycleFlow />,
  EpistemicsInterventions:  <EpistemicsInterventionsGrid />,
  AIForEpistemicsInterventions: <AIForEpistemicsInterventions />,
  BasinOfSanity:            <BasinOfSanityDiagram />,
  AIForEpistemicsFlowchart: <AIForEpistemicsFlowchart />,
  AssuranceLevel:           <AssuranceLevelDiagram />,
  AssuranceCurve:           <AssuranceCurveChart />,
  AssuranceCurveCompute:    <AssuranceCurveCompute />,
  InferenceOnlyAssurance:   <InferenceOnlyAssurance />,
  TakeoverRiskPlanA:        <TakeoverRiskPlanA />,
  TakeoverRiskPlanD:        <TakeoverRiskPlanD />,
  VerificationAssurance:    <RogueDeploymentCurve />,
  RogueDeployment:          <RogueDeploymentCurve />,
  MaxUndetectedRogue:       <MaxUndetectedRogue />,
  LoggingGranularity:       <LoggingGranularityDiagram />,
  ResearchTitration:        <ResearchTitrationDiagram />,
  ResearchTitrationApproaches: <ResearchTitrationApproaches />,
  WorkloadChunking:         <WorkloadChunkingDiagram />,
  FilteredTransparency:     <DatacenterArchitectureFilteredDiagram />,
  GovernanceAirgap:         <GovernanceAirgapDiagram />,
  RadicalTransparency:      <RadicalTransparencyDiagram />,
  TransparencyBenefits:     <TransparencyBenefitsDiagram />,
  SafetyCase:               <SafetyCaseChart />,
  SafetyCaseBuildingBlocks: <SafetyCaseBuildingBlocks />,
  SecurityTransparency:     <SecurityTransparencyDiagram />,
  VerificationDecisionTree: <VerificationDecisionTree />,
  VerificationFlow:         <VerificationFlowDiagram />,
  DatacenterFlow:           <DatacenterFlowDiagram />,
  TotalResearchTransparency: <DatacenterArchitectureDiagram />,
  VerificationTradeoffs:    <VerificationTradeoffsDiagram />,

  // Handoff timing / plan capabilities
  AbsoluteRisk:                 <AbsoluteRiskChart />,
  DealBreakdownByYear:          <DealBreakdownByYearChart />,
  HandoffRiskByYear:            <HandoffRiskByYearChart />,
  OptimalHandoff:               <OptimalHandoffChart />,
  HandoffOrNotWidget:           <HandoffOrNotWidget />,
  PlanCapabilities:             <PlanCapabilitiesChart />,
  PlanCapabilitiesMilestones:   <PlanCapabilitiesChartMilestones />,
  PlanAvsPlanDUplift:           <PlanAvsPlanDUpliftChart />,
  // Same chart on the capability-shaped uplift scale (milestone lines at the
  // shaped equivalents, Plan A plots the shaped row the side panel displays).
  PlanAvsPlanDUpliftShaped:     <PlanAvsPlanDUpliftChart scale="shaped" />,
  // Racing-takeoff chart (Plan D stub): AI software-R&D speedup vs. today,
  // milestones AC 5x -> ASI 10,000x, 2026-2031. Substituted for the stub's
  // exported screenshot via IMAGE_FIGURE_REPLACEMENTS (app/common/Markdown.tsx),
  // so gdoc re-imports can't overwrite it.
  RacingTakeoffUplift:          <RacingTakeoffUpliftChart />,
  SlowdownComparison:           <SlowdownComparisonChart />,
  PlanComparisonTable:          <PlanComparisonTable />,
  LikelihoodByAuthor:           <AuthorSlopeChart dataset={LIKELIHOOD} title="Probability each plan happens across authors" yAxisLabel="Probability plan happens" />,
  OutcomesByAuthor:             <AuthorSlopeChart dataset={OUTCOMES} title="Probability of avoiding misaligned takeover by plan" yAxisLabel="Probability of avoiding misaligned takeover" />,
  Stage1TargetSelection:        <Stage1TargetSelectionChart title="Deal decline and covert project risk trade off as speed varies" />,
  Stage1TargetSelectionSplitByTime: <Stage1TargetSelectionChart initialSplit showUnimpaired={false} title="Deal decline risk is more weighted toward Stage 1 than covert project risk" />,
  Stage1TargetSelectionValueLost: <Stage1TargetSelectionValueLostChart  />,
  DealDeclineCdf:               <DealDeclineCdfChart />,
  DissolutionTrajectory:        <DissolutionTrajectoryChart />,
  Stage1TargetSelectionV1:      <Stage1TargetSelectionV1Chart />,

  // Timeline / horizon
  TimeHorizon80p:           <TimeHorizon80pChart />,
  TimelineLine:             <TimelineLineDiagram />,
  TimelineLineDetailedClean: <TimelineLineDetailedClean />,
  // Rendered here with all sections collapsed as a fallback; the live story path
  // special-cases `timelineQuestions` in Markdown.tsx to read the `open="…"`
  // attribute (which subsections start expanded) and pass it through.
  timelineQuestions:        <TimelineQuestionsDiagram />,
  // Tiny at-a-glance takeoff sketches for narrow link-tooltip cards: capability
  // on a log y-axis (1× / 10× / 100× human) vs. time. Smooth accelerates
  // continuously; lumpy creeps then jumps. (simple-takeoff/)
  simpleSmoothTakeoff:      <SimpleSmoothTakeoff />,
  simpleLumpyTakeoff:       <SimpleLumpyTakeoff />,
  // US AI datacenter investment as % of GDP for the epoch.ai share-of-GDP link
  // tooltip: solid through mid-2026, dashed extrapolation to 2028. (simple-investment/)
  simpleAiInvestment:       <SimpleAiInvestment />,
  // High/critical CVEs per month from 21 notable orgs, spiking after the
  // Claude Mythos Preview announcement, for the epoch.ai "Mythos cyber
  // capabilities overhyped" link tooltip. (simple-cve-spike/)
  simpleCveSpike:           <SimpleCveSpike />,
  // Progress so far + three dashed S-curve continuations (flattens below human
  // level / above it / keeps climbing) for the astralcodexten "sigmoids won't
  // save you" link tooltip. (simple-sigmoid/)
  simpleSigmoidBranches:    <SimpleSigmoidBranches />,
  // Major AI ideas on a timeline with shrinking gaps, dashed into the future,
  // for the astralcodexten "new paradigms won't save you" link tooltip.
  // (simple-paradigms/)
  simpleParadigmTimeline:   <SimpleParadigmTimeline />,

  // Data-driven shares / automation / income (baked from xlsx)
  EconomyShare:                 <EconomyShareChart data={economyShareData} />,
  LaborIncomeShare:             <LaborIncomeShareChart data={laborIncomeShareData} />,
  OutputShare:                  <OutputShareChart data={outputShareData} />,
  TaskAutomation:               <TaskAutomationChart data={taskAutomationData} />,
  TaskAutomationLit:            <TaskAutomationLitChart data={taskAutomationData} />,
  AutomatableFigure:            <TaskAutomationLitChart data={taskAutomationData} />,
  TaskAutomationAIEra:          <TaskAutomationAIEraChart data={taskAutomationData} />,
  IncomeDistribution:           <IncomeDistributionPDFOverlay data={incomeData} />,

  // Energy / temperature / GDP projections
  EnergyModelSummary:           <EnergyModelSummary planAGdp={planAGdp} />,
  CovertProjectMap:             <CovertProjectMap />,
  EnergyGdpIndexProjected:      <EnergyGdpIndexChartProjected planAGdp={planAGdp} />,
  EnergyGdpLogLogProjected:     <EnergyGdpLogLogChartProjected planAGdp={planAGdp} />,
  EnergyTempByMethod:           <EnergyTempByMethodChart planAGdp={planAGdp} />,
  Co2WarmingTcre:               <Co2WarmingTcreChart planAGdp={planAGdp} />,
  Co2WarmingTimeSeries:         <Co2WarmingTimeSeriesChart planAGdp={planAGdp} />,
  GwpHistory:                   <GwpHistoryChart planAGdp={planAGdp} defaultWorldGdp={defaultWorldGdp} />,

  // US labor / income time series (self-contained, BLS + Census via FRED)
  UsEmployment:                 <UsEmploymentChart />,
  UsMedianIncome:               <UsMedianIncomeChart />,
  UsWorkersIncome:              <UsWorkersIncomeCombined />,
  OpenSourceSpectrum:           <OpenSourceSpectrum />,
  TransparencySpectrum:         <TransparencySpectrum />,

  // Company revenue (CSV + AI 2027 race-branch)
  CompanyRevenue:               <CompanyRevenueChart openai={companyRevenue.openai} anthropic={companyRevenue.anthropic} ai2027={ai2027Revenue} />,
  CompanyRevenueLinear:         <CompanyRevenueChartLinear openai={companyRevenue.openai} anthropic={companyRevenue.anthropic} ai2027={ai2027Revenue} />,
  CompanyRevenue2026:           <CompanyRevenue2026Chart openai={companyRevenue.openai} anthropic={companyRevenue.anthropic} ai2027={ai2027Revenue} />,

  // Tax / cap-and-trade
  TaxRevenue:                   <TaxRevenueChart data={taxRevenueData} />,
  CapAndTradeScenarios:         <CapAndTradeScenarios data={counterfactualData} />,
  CapAndTradeSummary:           <CapAndTradeScenarios data={counterfactualData} summary />,

  // Compute status / trajectory (CSV anchors)
  ComputeStatusGlobal2029:      <ComputeStatusGlobal2029 anchors={computeStatusAnchors} />,
  ComputeStatusPattern2029:     <ComputeStatusPattern2029 anchors={computeStatusAnchors} />,
  ComputeTrajectory:            <ComputeTrajectoryChart anchors={computeTrajectoryAnchors} />,

  // Labor population (3 variants used on /figures-code page)
  LaborPopulation:              <LaborPopulationChart data={planAPopulation} dataPlanD={defaultWorldPopulation} scale="log" scaleToggle={false} yMax={100e12} />,
  LaborPopulationLinear:        <LaborPopulationChart data={planAPopulation} scale="linear" linearMax={400e9} yLabel="human-equivalents" />,
  LaborPopulationLinear2035:    <LaborPopulationChart data={planAPopulation} scale="linear" yearMax={2035} linearMax={100e9} />,
};

// Directives whose figure component renders <FigureCaption>, and can therefore
// host a `:::caption` inline (between the chart and the attribution line — see
// the caption-pairing logic in app/common/Markdown.tsx). Figures NOT in this
// set caption themselves (in-SVG) or have no attribution line, so a `:::caption`
// after them renders as an ordinary block instead. Kept in sync with the
// components by scripts/checkFigureCaptionInline.mjs (run in CI).
export const FIGURE_DIRECTIVES_WITH_INLINE_CAPTION: ReadonlySet<string> = new Set([
  'AIDatacenters2029', 'AIDatacenters2029Hybrid', 'AIDatacenters2029Plain',
  'AifmEmbed', 'AlignmentOverTime', 'AllComputeEffective', 'AssuranceCurve',
  'AutomatableFigure', 'CapAndTradeScenarios', 'CapAndTradeSummary', 'CapexFunding',
  'ChinaCompute', 'ChipDeclarationResult', 'ChipDeclarationResultGlobal',
  'ChipLifecycle', 'ChipRestrictions2029', 'ChipRestrictions2032', 'ChipTracingTree', 'Co2WarmingTimeSeries',
  'CompanyConcentration', 'CompanyConcentrationTreemap', 'CompanyRevenueLinear',
  'ComputeGrowth', 'CostEfficiency', 'CovertComputeSankey', 'CovertOverview', 'DatacenterFlow', 'Deal2030',
  'FilteredTransparency', 'TotalResearchTransparency',
  'EUVSurcharge', 'EnergyGdpIndex', 'EnergyGdpIndexProjected', 'EnergyTempByMethod',
  'EpistemicsInterventions', 'GwpHistory', 'H100eBuildout', 'HandoffOrNotWidget',
  'ImpliedInvestment', 'IncomeDistribution', 'JonesAutomation', 'LaborIncomeShare', 'LaborPopulation',
  'LaborPopulationLinear', 'LaborPopulationLinear2035', 'LoggingGranularity',
  'MineralYearsLeft', 'NaiveBaselineFigure', 'OutputShare', 'RacingTakeoffUplift', 'PowerEfficiencyTrend',
  'RegionalOwnership', 'ResearchTitrationApproaches', 'ResourceCeilings', 'RogueDeployment',
  'SafetyCaseBuildingBlocks', 'SecurityTransparency', 'SocialCostCarbon', 'TakeoverRiskPlanA', 'TakeoverRiskPlanD',
  'TaskAutomationLit', 'TaxRevenue', 'TimeHorizon80p', 'TimelineLineDetailedClean',
  'TransparencyBenefits', 'UsefulnessCalculator', 'VerificationAssurance', 'VerificationDecisionTree', 'WorkloadChunking',
]);
